import {
  CasinoOutlined as RevolverIcon,
  SportsEsportsOutlined as MineIcon,
  AutoAwesomeOutlined as MRLIcon,
  RocketLaunchOutlined as MissileIcon,
} from "@mui/icons-material";
import { colors } from "../theme/colors";
import { networkID } from "./utils";

/**
 * Returns native blockchain currency ticker according to chain network ID.
 */
export const getNetworkCurrency = (netId) => {
  const id = Number(netId || networkID);
  if (id === 137 || id === 80002 || id === 80001) return "POL";
  if (id === 1 || id === 5 || id === 11155111) return "ETH";
  if (id === 56 || id === 97) return "BNB";
  return "POL";
};

export const DEFAULT_CURRENCY = getNetworkCurrency(networkID);
export const DEFAULT_TOKEN_SYMBOL = "BET";


/**
 * Network name lookup by chain network ID.
 */
export const NETWORK_NAMES = {
  137: "Polygon Mainnet",
  80002: "Polygon Amoy Testnet",
  80001: "Polygon Mumbai Testnet",
  1: "Ethereum Mainnet",
  5: "Goerli Testnet",
  11155111: "Sepolia Testnet",
  56: "BNB Smart Chain Mainnet",
  97: "BNB Smart Chain Testnet",
};

/**
 * Returns network name according to chain network ID.
 */
export const getNetworkName = (netId) => {
  const id = Number(netId || networkID);
  if (NETWORK_NAMES[id]) return NETWORK_NAMES[id];
  if (id) return `Network ${id}`;
  return "Unknown Network";
};

/**
 * Blockchain Explorer Constants & Helper
 */
export const EXPLORER_BASE_URL = "https://testnet.bscscan.com/address/";
export const EXPLORER_LABEL = "View on Bscscan";

export const getExplorerAddressUrl = (address) =>
  address ? `${EXPLORER_BASE_URL}${address}` : "";

export const Path = {
  root: "/",
  login: "/login",
  help: "/help",
  about: "/aboutus",
  register: "/registers",
  registerasorg: "/register",
  singlelottery: "/lottery/:id",
  admin: "/admins",
  adminorglist: "/admin/creatorslist",
  adminlotterylist: "/admin/lotterylist",
  adminlotterydetail: "/admin/lotterydetails/:id",
  org: "/creators",
  orgcreatelottery: "/creator/createlottery",
  orglotterydetail: "/creator/lotterydetails/:id",
  orgmylottery: "/creator/mylottery",
  alllottery: "/all-lottery",
  result: "/lottery-result",
  profile: "/profile",
  ThreeSlotlottery: "/all-three-slotlottery",
  ThreeSlotlotteryDetails: "/three-slotlottery/:id",
  FiveSlotlottery: "/all-five-slotlottery",
  FiveSlotlotteryDetails: "/five-slotlottery/:id",
  Spinnerlottery: "/spinnerlottery",
  adminpartnerslist: "/admin/partnerslist",
  minelottery: "/minelottery/:id",
  allminelottery: "/all-minelottery",
  missilelottery: "/missilelottery/:id",
  allmissilelottery: "/all-missilelottery",
  allgiftcards: "/all-giftcards",
  giftcard: "/giftcard/:id",
  orgcreatecard: "/creator/create-giftcard",
  mygiftcard: "/creator/mygiftcard",
  admingiftcard: "/admin/giftcardlist",
  admincarddetails: "/admin/giftcarddetails/:id",
  orgcarddetails: "/creator/giftcarddetails/:id",
  adminNews: "/admin/news",
  notifications: "/notifications",
  adminNotifications: "/admin/notifications",
  orgNotifications: "/creator/notifications",
  news: "/all-news",
  newsId: "/news/:id",
  lotterydetail: "/lotterydetails/:id",
  allLotteries: "/all-lotteries",
  terms: "/terms",

  // Base paths without param markers (for constructing links or checking location.pathname)
  adminLotteryDetailsBase: "/admin/lotterydetails",
  orgLotteryDetailsBase: "/creator/lotterydetails",
  adminGiftCardDetailsBase: "/admin/giftcarddetails",
  orgGiftCardDetailsBase: "/creator/giftcarddetails",
  userLotteryDetailsBase: "/lotterydetails",
  userGiftCardBase: "/giftcard",
  newsBase: "/news",
  revolverLotteryBase: "/lottery",
  mineLotteryBase: "/minelottery",
  missileLotteryBase: "/missilelottery",
  threeSlotLotteryBase: "/three-slotlottery",
  fiveSlotLotteryBase: "/five-slotlottery",

  // Helper Methods for Dynamic Links
  getLotteryDetails: (id) => `/lotterydetails/${id}`,
  getAdminLotteryDetails: (id) => `/admin/lotterydetails/${id}`,
  getCreatorLotteryDetails: (id) => `/creator/lotterydetails/${id}`,
  getGiftCardDetails: (id) => `/giftcard/${id}`,
  getAdminGiftCardDetails: (id) => `/admin/giftcarddetails/${id}`,
  getCreatorGiftCardDetails: (id) => `/creator/giftcarddetails/${id}`,
  getCreatorCreateThreeSlot: () => `/creator/createlottery?type=3slot`,
  getCreatorCreateFiveSlot: () => `/creator/createlottery?type=5slot`,
  getNewsDetails: (id) => `/news/${id}`,
  getRevolverLottery: (id) => `/lottery/${id}`,
  getMineLottery: (id) => `/minelottery/${id}`,
  getMissileLottery: (id) => `/missilelottery/${id}`,
  getThreeSlotLottery: (id) => `/three-slotlottery/${id}`,
  getFiveSlotLottery: (id) => `/five-slotlottery/${id}`,
  getLotteryPlayUrl: (id, type) => {
    const typeNum = Number(type);
    if (typeNum === 0) return "/spinnerlottery";
    if (typeNum === 1) return `/minelottery/${id}`;
    if (typeNum === 3) return `/missilelottery/${id}`;
    if (typeNum === 4) return `/three-slotlottery/${id}`;
    if (typeNum === 5) return `/five-slotlottery/${id}`;
    return `/lottery/${id}`;
  },
  getProfile: () => "/profile",
  getNotifications: (role) => {
    if (role === "admin") return "/admin/notifications";
    if (role === "creator") return "/creator/notifications";
    return "/notifications";
  },
};

/**
 * Centralized Lottery Game Type IDs
 */
export const LOTTERY_TYPE_IDS = {
  SPINBET: 0,
  MINEBET: 1,
  MEGABET: 2,
  ROCKETBET: 3,
  THREESLOT: 4,
  FIVESLOT: 5,
};

/**
 * Centralized Lottery Game Names Constants
 */
export const LOTTERY_NAMES = {
  SPINBET: "SpinBET",
  MINEBET: "MineBET",
  MEGABET: "MegaBET",
  ROCKETBET: "RocketBET",
  THREESLOT: "3-SlotBET",
  FIVESLOT: "5-SlotBET",
};

// Aliases and individual exports for flexible importing
export const GAME_NAMES = LOTTERY_NAMES;
export const SPINBET = LOTTERY_NAMES.SPINBET;
export const MINEBET = LOTTERY_NAMES.MINEBET;
export const MEGABET = LOTTERY_NAMES.MEGABET;
export const ROCKETBET = LOTTERY_NAMES.ROCKETBET;
export const THREESLOT = LOTTERY_NAMES.THREESLOT;
export const FIVESLOT = LOTTERY_NAMES.FIVESLOT;

/**
 * Centralized Lottery Game Type Mapper
 */
export const lotteryTypeMapper = {
  [LOTTERY_TYPE_IDS.SPINBET]: LOTTERY_NAMES.SPINBET,
  [LOTTERY_TYPE_IDS.MINEBET]: LOTTERY_NAMES.MINEBET,
  [LOTTERY_TYPE_IDS.MEGABET]: LOTTERY_NAMES.MEGABET,
  [LOTTERY_TYPE_IDS.ROCKETBET]: LOTTERY_NAMES.ROCKETBET,
  [LOTTERY_TYPE_IDS.THREESLOT]: LOTTERY_NAMES.THREESLOT,
  [LOTTERY_TYPE_IDS.FIVESLOT]: LOTTERY_NAMES.FIVESLOT,
};

/**
 * Returns lottery type display name by type ID from lotteryTypeMapper.
 */
export const getLotteryTypeName = (typeId) => {
  if (lotteryTypeMapper[typeId] !== undefined) return lotteryTypeMapper[typeId];
  return typeId !== undefined && typeId !== null ? `Lottery Type ${typeId}` : "Lottery";
};

/**
 * Centralized Lottery Result & Lifecycle Status Mapper
 */
export const resultStatusTypeMapper = {
  0: "Open",
  1: "Close",
  2: "Result Done",
  3: "Rollover",
  4: "Blocked",
  5: "Rollover Open",
  6: "Creation in progress",
};

/**
 * Sort Options for Lotteries
 */
export const LOTTERY_SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "highestPrize", label: "Highest Prize" },
  { value: "lowestPrize", label: "Lowest Prize" },
  { value: "entryFee", label: "Entry Fee (High to Low)" },
];

export const lotterySortOptions = LOTTERY_SORT_OPTIONS;
export const sortOptions = LOTTERY_SORT_OPTIONS;

/**
 * Lottery Game Type Configurations
 */
export const LOTTERY_TYPES = {
  [LOTTERY_TYPE_IDS.SPINBET]: {
    name: LOTTERY_NAMES.SPINBET,
    shortName: LOTTERY_NAMES.SPINBET,
    color: colors.primary.main,
    bgColor: colors.blue[50],
    icon: RevolverIcon,
    gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
  },
  [LOTTERY_TYPE_IDS.MINEBET]: {
    name: LOTTERY_NAMES.MINEBET,
    shortName: LOTTERY_NAMES.MINEBET,
    color: colors.purple[600],
    bgColor: colors.purple[50],
    icon: MineIcon,
    gradient: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
  },
  [LOTTERY_TYPE_IDS.MEGABET]: {
    name: LOTTERY_NAMES.MEGABET,
    shortName: LOTTERY_NAMES.MEGABET,
    color: colors.emerald[600],
    bgColor: colors.emerald[50],
    icon: MRLIcon,
    gradient: "linear-gradient(135deg, #10b981 0%, #047857 100%)",
  },
  [LOTTERY_TYPE_IDS.ROCKETBET]: {
    name: LOTTERY_NAMES.ROCKETBET,
    shortName: LOTTERY_NAMES.ROCKETBET,
    color: colors.amber[600],
    bgColor: colors.amber[50],
    icon: MissileIcon,
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
  },
  [LOTTERY_TYPE_IDS.THREESLOT]: {
    name: LOTTERY_NAMES.THREESLOT,
    shortName: LOTTERY_NAMES.THREESLOT,
    color: colors.purple[600],
    bgColor: colors.purple[50],
    icon: RevolverIcon,
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
  },
  [LOTTERY_TYPE_IDS.FIVESLOT]: {
    name: LOTTERY_NAMES.FIVESLOT,
    shortName: LOTTERY_NAMES.FIVESLOT,
    color: colors.blue[600],
    bgColor: colors.blue[50],
    icon: RevolverIcon,
    gradient: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
  },
};

/**
 * Lottery Status Configurations
 */
export const STATUS_CONFIG = {
  0: { label: resultStatusTypeMapper[0] || "Open", variant: "active", color: colors.emerald[600], bg: colors.emerald[50] },
  1: { label: resultStatusTypeMapper[1] || "Closed", variant: "ended", color: colors.slate[600], bg: colors.slate[100] },
  2: { label: resultStatusTypeMapper[2] || "Result Done", variant: "ended", color: colors.slate[700], bg: colors.slate[100] },
  3: { label: resultStatusTypeMapper[3] || "Rollover", variant: "warning", color: colors.amber[700], bg: colors.amber[50] },
  4: { label: resultStatusTypeMapper[4] || "Blocked", variant: "danger", color: colors.red[600], bg: colors.red[50] },
  5: { label: resultStatusTypeMapper[5] || "Rollover Open", variant: "active", color: colors.cyan[700], bg: colors.cyan[50] },
  6: { label: resultStatusTypeMapper[6] || "Creation in progress", variant: "pending", color: colors.purple[600], bg: colors.purple[50] },
};